import * as React from "react";
import { ReactNode, useMemo } from "react";
import { NavLink } from "react-router-dom";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import { snakeCase } from "lodash";

import useIsMobile from "hooks/use-is-mobile.hook";

import { useSideMenuFlyout } from "./side-menu-flyout.component";

export const SIDE_MENU_ROW_HEIGHT_PX = 32;

export type SideMenuItemProps = {
  isDefaultPath?: boolean;
  /** Where the row navigates; with `onClick` instead, the row is a button (never active). */
  to?: string;
  onClick?: () => void;
  primary: string;
  Icon: React.ElementType;
  isFallbackPath?: boolean;
  badgeContent?: ReactNode;
  tooltip?: string;
  /** Rail state: the icon alone, its name and badge in the rail's hover card. */
  collapsed?: boolean;
  /** Suno-staff rows (Administration) render in the amber staff accent. */
  staff?: boolean;
  /** The row's group heading ("Front desk"); the rail's hover card shows it. */
  group?: string;
};

/**
 * The rail's hover card for one row: the handlers that open it, or `null` when the
 * row is expanded (the tooltip names it then). Deliberately no router hooks: the
 * card reads "is this the current page" from the row's own `aria-current` and
 * "go there" by clicking the row's own link, both at hover time, so the 25 rows
 * carry no extra subscription and do no extra work on any render.
 */
function useRailHoverCard({
  collapsed,
  primary,
  group,
  tooltip,
  badgeContent,
  staff
}: Required<Pick<SideMenuItemProps, "collapsed" | "staff">> &
  Pick<SideMenuItemProps, "primary" | "group" | "tooltip" | "badgeContent">) {
  const flyout = useSideMenuFlyout();
  if (!collapsed || !flyout) return null;

  const show = (event: React.SyntheticEvent<HTMLElement>) => {
    const row = event.currentTarget;
    flyout.show(row, {
      label: primary,
      eyebrow: group,
      description: tooltip,
      badgeContent:
        typeof badgeContent === "number" || typeof badgeContent === "string"
          ? badgeContent
          : undefined,
      staff,
      active: row.querySelector('[aria-current="page"]') !== null,
      onActivate: () => row.querySelector<HTMLElement>("a, button")?.click()
    });
  };
  return {
    onMouseEnter: show,
    onFocus: show,
    onMouseLeave: () => flyout.hide(),
    onBlur: () => flyout.hide()
  };
}

/** The row's count: beside the label, or on the icon's corner in the rail. */
function SideMenuItemBadge({
  badgeContent,
  collapsed
}: {
  badgeContent: ReactNode;
  collapsed: boolean;
}) {
  const theme = useTheme();
  if (!badgeContent) return null;
  return (
    <Badge
      badgeContent={badgeContent}
      color="primary"
      max={99}
      sx={
        collapsed
          ? {
              // On the icon's top-right corner, ringed in the rail's plum so it
              // reads as sitting on top of the glyph rather than colliding with it.
              position: "absolute",
              left: "calc(50% + 4px)",
              top: "1px",
              "& .MuiBadge-badge": {
                position: "static",
                transform: "none",
                minWidth: "18px",
                height: "18px",
                px: "5px",
                boxShadow: `0 0 0 2px ${theme.palette.sideMenu.main}`
              }
            }
          : {
              flex: "0 0 auto",
              "& .MuiBadge-badge": { position: "static", transform: "none" }
            }
      }
    />
  );
}

/**
 * One row of the main side menu (ENG-2535): a 20px icon beside a 15px label,
 * 32px tall in both states. The active row gets a lilac tint and a 3px bar on its
 * left edge. In the rail the row is the icon alone, centred, with its badge on the
 * icon's corner; the name, group, badge and description show in the rail's hover
 * card (`SideMenuFlyoutProvider`) rather than a tooltip.
 */
export default function SideMenuItem({
  isDefaultPath = false,
  to,
  onClick,
  primary,
  Icon,
  isFallbackPath = false,
  badgeContent,
  tooltip,
  collapsed = false,
  staff = false,
  group
}: SideMenuItemProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  // The hover card replaces the tooltip in the rail only.
  const railHandlers = useRailHoverCard({
    collapsed,
    primary,
    group,
    tooltip,
    badgeContent,
    staff
  });

  const accent = staff
    ? theme.palette.sideMenu.staffAccent
    : theme.palette.sideMenu.itemBgcolorSelected;
  const restingColor = staff
    ? theme.palette.sideMenu.staffAccent
    : theme.palette.neutral[500];

  const rowSx = useMemo(
    () => (isActive: boolean) => ({
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: 1.25,
      height: `${SIDE_MENU_ROW_HEIGHT_PX}px`,
      // The row is the flex child of its NavLink (a row-direction flex box), so this
      // basis is horizontal: fill the link, never a fixed width.
      flex: "1 1 auto",
      minWidth: 0,
      boxSizing: "border-box",
      px: collapsed ? 0 : 1,
      borderRadius: "8px",
      textDecoration: "none",
      overflow: "hidden",
      color: isActive ? theme.palette.common.white : restingColor,
      backgroundColor: isActive
        ? alpha(accent, staff ? 0.12 : 0.18)
        : "transparent",
      "&:hover": {
        backgroundColor: isActive
          ? alpha(accent, staff ? 0.16 : 0.24)
          : alpha(theme.palette.common.white, 0.07)
      },
      "&::before": {
        content: "''",
        display: isActive ? "block" : "none",
        position: "absolute",
        left: 0,
        top: collapsed ? "8px" : "6px",
        bottom: collapsed ? "8px" : "6px",
        width: "3px",
        borderRadius: "2px",
        backgroundColor: accent
      }
    }),
    [collapsed, theme.palette.common.white, restingColor, accent, staff]
  );

  const renderRow = (active: boolean) => (
    <Box
      sx={rowSx(active)}
      aria-current={active ? "page" : undefined}
      data-active={active ? "true" : undefined}
    >
      <Icon sx={{ fontSize: collapsed ? 22 : 20, flex: "0 0 auto" }} />
      {!collapsed && (
        <Typography
          component="span"
          noWrap
          sx={{
            // Basis `auto`, not 0: the expanded menu is `fit-content`, and only an
            // auto basis lets the label's own width count toward that.
            flex: "1 1 auto",
            minWidth: 0,
            fontSize: "15px",
            lineHeight: 1,
            fontWeight: active ? "fontWeightBold" : "fontWeightRegular",
            color: "inherit"
          }}
        >
          {primary}
        </Typography>
      )}
      <SideMenuItemBadge badgeContent={badgeContent} collapsed={collapsed} />
    </Box>
  );

  const listItem = (
    <ListItem
      disablePadding
      data-testid={snakeCase(primary)}
      data-side-menu-badge={
        typeof badgeContent === "number" && badgeContent > 0
          ? badgeContent
          : undefined
      }
      {...(railHandlers ?? {})}
      sx={{
        // The link is what takes focus; the browser's thin blue `outline: auto` fails
        // 3:1 on plum, so draw the ring in the row's own accent.
        "& a:focus-visible, & button:focus-visible": {
          outline: `2px solid ${accent}`,
          outlineOffset: "-2px",
          borderRadius: "8px"
        }
      }}
    >
      {to === undefined ? (
        <Box
          component="button"
          type="button"
          onClick={onClick}
          aria-label={collapsed ? primary : undefined}
          sx={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            p: 0,
            border: 0,
            background: "transparent",
            font: "inherit",
            textAlign: "left",
            cursor: "pointer"
          }}
        >
          {renderRow(false)}
        </Box>
      ) : (
        <NavLink
          to={to}
          aria-label={collapsed ? primary : undefined}
          style={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            textDecoration: "none"
          }}
        >
          {({ isActive }) =>
            renderRow(isActive || (isFallbackPath && isDefaultPath))
          }
        </NavLink>
      )}
    </ListItem>
  );

  // The rail's hover card replaces the tooltip there; expanded rows keep it for
  // the one-line description.
  if (railHandlers) return listItem;

  return (
    <Tooltip
      title={
        <Typography variant="body1">
          <strong>{primary}</strong>
          {tooltip ? ` — ${tooltip}` : ""}
        </Typography>
      }
      placement="right"
      disableInteractive
      open={isMobile ? false : undefined}
    >
      {listItem}
    </Tooltip>
  );
}
